// Reusable multi-assignee picker: trigger button shows avatar stack of the
// current selection, click opens a checkbox list. Order of `value` is the
// source of truth — the first id is the "primary" assignee that views can
// highlight. Toggling an entry preserves the existing order.
//
// #875: the open menu has a search field (filters name + email, from the
// first character) and full keyboard navigation (Arrow Up/Down + Enter +
// Esc). When a `projectId` is given, the default candidate list is that
// project's members instead of every active user — with a visible "Alle
// Nutzer anzeigen" escape hatch, and a hard guarantee that an already-
// assigned non-member is NEVER silently dropped from the list.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { ChevronDown } from "lucide-preact";
import { createPortal } from "preact/compat";
import { useUsers, findUser } from "../store/users";
import { useProjectMembersLookup } from "../store/members";
import { useMe } from "../lib/auth";
import { UserAvatar } from "./UserAvatar";
import { AvatarStack } from "./AvatarStack";
import { BotBadge } from "./BotBadge";
import type { UserPublic } from "@juuplan/shared";

interface Props {
  value: number[];
  onChange: (next: number[]) => void | Promise<void>;
  /** Render the trigger button compact (used in TaskRow). */
  compact?: boolean;
  /** Aria-label for the trigger; default is generic. */
  label?: string;
  /**
   * Text shown when nothing is selected. Default passt fuer Zustaendige; das
   * Beobachter-Feld braucht eine andere Formulierung.
   */
  emptyLabel?: string;
  /**
   * Einzelauswahl (#854, Owner-Feld im Projekt-Kopf): ein Klick SETZT die
   * Auswahl auf genau diese Person und schliesst das Menue, statt zu toggeln.
   * Suche und Tastaturbedienung bleiben identisch — deshalb dieselbe Komponente
   * und kein zweiter Picker. Abwaehlen ist in diesem Modus nicht moeglich: ein
   * Projekt ohne Owner gibt es nicht.
   */
  single?: boolean;
  /**
   * When given, the menu defaults to this project's members instead of every
   * active user (#875). Optional so existing call sites (e.g. TaskModal,
   * personal-task lists with no project) keep today's "all users" behavior
   * untouched.
   */
  projectId?: number;
  /**
   * Hard-scope the candidate list to exactly these user ids (#854, used by the
   * personal-task rail: only members of the list's SPACE are assignable, the
   * server rejects anyone else with a 400). Unlike `projectId` scoping there is
   * NO "Alle Nutzer anzeigen" escape hatch and no "kein Mitglied" hint — every
   * entry shown is assignable. Already-selected users still stay visible.
   */
  candidateIds?: number[];
  /** Fires whenever the menu transitions from open to closed, for any reason
   *  (backdrop click, Esc, re-clicking the trigger). Used by callers (e.g.
   *  BulkActionToolbar) that need to react once the user is done picking. */
  onClose?: () => void;
}

let idSeq = 0;
function useStableId(prefix: string): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) ref.current = `${prefix}-${++idSeq}`;
  return ref.current;
}

// ---------- pure helpers (exported for unit tests) ----------

/**
 * Case-insensitive substring match on name OR email, from the first
 * character (not prefix-only) — so "oe" finds "Moritz" and ".com" finds
 * anyone by domain. An empty/whitespace query returns the list unchanged.
 */
export function filterUsersBySearch(users: UserPublic[], query: string): UserPublic[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter(
    (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  );
}

/**
 * The candidate list for the picker's default (members-first) mode: project
 * members plus any already-selected user who is NOT a member. The latter is
 * the #875 acceptance criterion — an assignee must never silently vanish
 * from the list just because they are not (or no longer) a project member.
 *
 * `memberIds === null` means no scoping is active (no projectId, "Alle
 * Nutzer anzeigen" toggled on, or the member list has not loaded yet) —
 * every user passed in is a candidate, matching today's behavior.
 *
 * Bots (Migration 0085) sind nie Projekt-Mitglieder — ihre Rechte kommen vom
 * Owner, nicht aus der Mitgliederliste. Die Member-Scoping-Regel wuerde sie
 * damit aus jedem Projekt-Picker werfen, obwohl genau das der Zweck eines Bots
 * ist. Sie sind deshalb Kandidat, wenn ihr Owner Mitglied ist oder wenn es die
 * eigenen Bots sind (`meId`) — fremde Bots bleiben aus fremden Projekten raus.
 */
export function visibleAssigneeCandidates(
  allUsers: UserPublic[],
  memberIds: Set<number> | null,
  selectedIds: number[],
  meId?: number | null
): UserPublic[] {
  if (memberIds === null) return allUsers;
  const selected = new Set(selectedIds);
  return allUsers.filter((u) => {
    if (memberIds.has(u.id) || selected.has(u.id)) return true;
    if (u.is_bot && u.bot_owner_id != null) {
      return memberIds.has(u.bot_owner_id) || u.bot_owner_id === meId;
    }
    return false;
  });
}

/**
 * Die neue Auswahl, wenn ein Eintrag angeklickt wird.
 *
 * `single` (#854, Owner-Feld): der Klick SETZT auf genau diese Person, und ein
 * Klick auf die bereits gewaehlte Person aendert nichts — in diesem Modus darf
 * die Auswahl NIE leer werden (ein Projekt ohne Owner gibt es nicht).
 * Mehrfachauswahl: toggeln, Reihenfolge bleibt erhalten (das erste Element ist
 * die primaere Person).
 */
export function nextSelection(
  current: number[],
  userId: number,
  single: boolean
): number[] {
  // Identitaet (dasselbe Array) nur bei einem Klick auf die EINZIGE bereits
  // gewaehlte Person — daran erkennt der Aufrufer den No-op. Eine Altauswahl
  // mit mehreren Eintraegen wird auf genau eine Person zusammengeraeumt, sonst
  // liesse `single` mehr als einen Wert stehen.
  if (single) {
    return current.length === 1 && current[0] === userId ? current : [userId];
  }
  return current.includes(userId)
    ? current.filter((id) => id !== userId)
    : [...current, userId];
}

export function AssigneePicker(props: Props): preact.JSX.Element {
  const {
    value,
    onChange,
    compact = false,
    label = "Assignees",
    emptyLabel = "— Nicht zugewiesen —",
    single = false,
    projectId,
    candidateIds,
    onClose,
  } = props;
  const users = useUsers();
  const me = useMe();
  const membersLookup = useProjectMembersLookup(projectId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);
  const listId = useStableId("assignee-listbox");
  const optionId = (idx: number): string => `${listId}-opt-${idx}`;
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; placeAbove: boolean; maxH: number }>({
    top: 0,
    left: 0,
    placeAbove: false,
    maxH: 288,
  });

  // Re-compute menu position when it opens or window scrolls/resizes so the
  // portal-rendered list always sits flush against the trigger without being
  // clipped by ancestor `overflow:hidden` containers. Clamps to the viewport on
  // all four edges so the list never runs off-screen on a narrow phone.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const recompute = (): void => {
      const r = triggerRef.current!.getBoundingClientRect();
      const menuH = 288; // matches max-h-72 below
      const gap = 4;
      const margin = 8;
      const menuW = 256; // w-64
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const placeAbove = spaceBelow < menuH && spaceAbove > spaceBelow;
      const avail = (placeAbove ? spaceAbove : spaceBelow) - gap - margin;
      setMenuPos({
        top: placeAbove ? r.top - gap : r.bottom + gap,
        left: Math.max(margin, Math.min(r.left, window.innerWidth - menuW - margin)),
        placeAbove,
        maxH: Math.max(160, Math.min(menuH, avail)),
      });
    };
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  // Esc schließt zusätzlich zum Backdrop-Klick, und gibt den Fokus an den
  // Trigger zurück (egal ob der Fokus im Suchfeld oder auf einer Checkbox lag).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Autofocus the search field whenever the menu opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Fresh search/filter state each time the menu opens; also notify the
  // caller once the menu actually closes (any reason).
  useEffect(() => {
    if (!open) {
      setQuery("");
      setShowAll(false);
      setActiveIndex(-1);
    }
    if (wasOpenRef.current && !open) onClose?.();
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query, showAll]);

  const assigned: UserPublic[] = value
    .map((id) => findUser(id))
    .filter((u): u is UserPublic => u !== null);
  const primary = assigned[0];
  const extraCount = Math.max(0, assigned.length - 3);

  // Membership is known once the (optional) project's members have loaded.
  // Used both for the hint badge ("kein Mitglied") and, unless "Alle Nutzer
  // anzeigen" is on, to scope the default candidate list.
  const knownMemberIds = useMemo<Set<number> | null>(() => {
    if (projectId === undefined || membersLookup.loading) return null;
    return new Set(membersLookup.items.map((m) => m.user.id));
  }, [projectId, membersLookup.loading, membersLookup.items]);
  // Hard scope (candidateIds) wins over the project-member default and over
  // "Alle Nutzer anzeigen" — those users are simply not assignable here.
  const hardScopeIds = useMemo<Set<number> | null>(
    () => (candidateIds ? new Set(candidateIds) : null),
    [candidateIds]
  );
  const scopeMemberIds = hardScopeIds ?? (showAll ? null : knownMemberIds);

  const candidates = visibleAssigneeCandidates(
    users.items,
    scopeMemberIds,
    value,
    me.user?.id ?? null
  );
  const filtered = filterUsersBySearch(candidates, query);

  useEffect(() => {
    setActiveIndex((i) => (i >= filtered.length ? filtered.length - 1 : i));
  }, [filtered.length]);

  const toggle = (userId: number): void => {
    const next = nextSelection(value, userId, single);
    if (next !== value) void onChange(next);
    if (single) {
      // Einzelauswahl ist mit einem Klick fertig — Menue zu, Fokus zurueck.
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const onSearchKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        toggle(filtered[activeIndex].id);
      }
    }
  };

  const triggerCls = compact
    ? "w-full inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[28px]"
    : "w-full inline-flex items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]";

  const avatarPx = compact ? 28 : 28;

  return (
    <div class="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        class={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        title={single ? `${label} wählen` : `${label} wählen — mehrere möglich`}
      >
        {assigned.length === 0 ? (
          <span class="text-[color:var(--color-on-surface-variant)]">{emptyLabel}</span>
        ) : (
          <>
            <span class="flex gap-0.5">
              {assigned.slice(0, 3).map((u) => (
                <UserAvatar
                  key={u.id}
                  userId={u.id}
                  size={avatarPx}
                  className="border-2 border-[color:var(--color-surface-container-lowest)]"
                />
              ))}
              {extraCount > 0 && (
                <span
                  class="shrink-0 rounded-full bg-[color:var(--color-surface-container-high)] font-semibold flex items-center justify-center border-2 border-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)]"
                  style={{
                    width: `${avatarPx}px`,
                    height: `${avatarPx}px`,
                    fontSize: `${Math.max(9, Math.round(avatarPx * 0.42))}px`,
                  }}
                  aria-hidden="true"
                >
                  +{extraCount}
                </span>
              )}
            </span>
            {primary && assigned.length === 1 && (
              <>
                <span class="truncate">{primary.name}</span>
                {primary.is_bot && <BotBadge ownerId={primary.bot_owner_id} />}
              </>
            )}
            {assigned.length > 1 && (
              <span class="truncate text-[color:var(--color-on-surface-variant)]">
                {assigned.length} Personen
              </span>
            )}
          </>
        )}
        {/* #854: Ohne diesen Chevron sah der Ausloeser bei GENAU EINER Person
            (Avatar + Name) wie ein statisches Label aus — Mehrfachauswahl war
            vorhanden, aber unauffindbar. Bei 0 Personen und ab 2 sagte der Text
            es implizit, bei 1 nicht. */}
        <ChevronDown
          size={14}
          class={`ml-auto shrink-0 text-[color:var(--color-on-surface-variant)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      {open &&
        createPortal(
          <>
            <div
              class="fixed inset-0 z-[60]"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              class="fixed z-[70] w-64 overflow-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 py-1 text-xs"
              style={{
                top: `${menuPos.top}px`,
                left: `${menuPos.left}px`,
                maxHeight: `${menuPos.maxH}px`,
                transform: menuPos.placeAbove ? "translateY(-100%)" : "translateY(0)",
              }}
            >
              <div class="px-2 pt-1 pb-1.5">
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder="Suchen…"
                  aria-label="Nutzer suchen"
                  aria-controls={listId}
                  aria-activedescendant={
                    activeIndex >= 0 && activeIndex < filtered.length ? optionId(activeIndex) : undefined
                  }
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                />
              </div>
              {projectId !== undefined && hardScopeIds === null && (
                <label class="flex items-center gap-2 px-3 py-1.5 border-b border-[color:var(--color-outline-variant)] cursor-pointer text-micro text-[color:var(--color-on-surface-variant)]">
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => setShowAll((e.currentTarget as HTMLInputElement).checked)}
                    class="accent-[color:var(--color-primary)]"
                  />
                  Alle Nutzer anzeigen
                </label>
              )}
              <div id={listId} role="listbox" aria-multiselectable={!single}>
                {filtered.length === 0 && (
                  <div class="px-3 py-2 text-[color:var(--color-on-surface-variant)]">
                    {users.items.length === 0 ? "Keine Nutzer geladen" : "Keine Treffer"}
                  </div>
                )}
                {filtered.map((u, idx) => {
                  const checked = value.includes(u.id);
                  const isActive = idx === activeIndex;
                  // Bots sind nie Mitglieder — der Hinweis waere bei ihnen
                  // immer da und trotzdem bedeutungslos (Rechte vom Owner).
                  const notMember =
                    hardScopeIds === null &&
                    knownMemberIds !== null &&
                    !knownMemberIds.has(u.id) &&
                    !u.is_bot;
                  return (
                    <label
                      key={u.id}
                      id={optionId(idx)}
                      role="option"
                      aria-selected={checked}
                      class={`flex items-center gap-2 px-3 py-1.5 cursor-pointer ${
                        isActive
                          ? "bg-[color:var(--color-surface-container-low)]"
                          : "hover:bg-[color:var(--color-surface-container-low)]"
                      }`}
                    >
                      <input
                        type={single ? "radio" : "checkbox"}
                        checked={checked}
                        onChange={() => toggle(u.id)}
                        class="accent-[color:var(--color-primary)]"
                      />
                      <UserAvatar userId={u.id} size={20} />
                      <span class="truncate text-[color:var(--color-on-surface)]">
                        {u.name}
                      </span>
                      {u.is_bot && <BotBadge ownerId={u.bot_owner_id} />}
                      <span class="ml-auto flex items-center gap-1 shrink-0">
                        {notMember && (
                          <span
                            class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]/70"
                            title="Kein Projekt-Mitglied"
                          >
                            kein Mitglied
                          </span>
                        )}
                        {!single && checked && value[0] === u.id && (
                          <span class="text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                            Primär
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

export { AvatarStack };
